import React, { FC, useCallback, useState } from "react";
import gql from "graphql-tag";
import { GraphqlOperations, TranslationSetInput } from "@src/types.generated";
import { TranslationSetFragment } from "@src/fragments.generated";
import {
  useTranslationSetQuery,
  useUpdateTranslationSetMutation,
} from "@src/features/app/dialogs/translationSetDialogs/TranslateTextDialog.generated";

import { useToast } from "@src/lib/hooks/useToast";
import TranslationTextField from "@src/features/contentDetail/ui/TranslationTextField";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translationSetUuid: string;
  label: string;
  title: string;
  onSave?: (newTs: TranslationSetFragment) => void;
  saveButtonDataTestId?: string;
};

const TranslateTextDialog: FC<Props> = ({
  open,
  onOpenChange,
  translationSetUuid,
  label,
  title,
  onSave,
  saveButtonDataTestId,
}) => {
  const { data } = useTranslationSetQuery({
    variables: { uuid: translationSetUuid },
    onError() {
      addErrorToast({ callsite: "translate_text_dialog" });
    },
    onCompleted(data) {
      setTranslationSetInput(data.TranslationSet);
    },
  });
  const { addErrorToast } = useToast();
  const [updateTranslationSetMutation] = useUpdateTranslationSetMutation({
    refetchQueries: [
      GraphqlOperations.Query.CourseVersion,
      GraphqlOperations.Query.LibraryItemDetailPage_LibraryItem,
      GraphqlOperations.Query.TranslationSet,
    ],
    onCompleted(data) {
      if (data.updateTranslationSet.success) {
        onOpenChange(false);
        if (onSave) {
          onSave(data.updateTranslationSet.translationSet!);
        }
      } else {
        addErrorToast(data);
      }
    },
    onError() {
      addErrorToast({ callsite: "translate_text_dialog" });
    },
  });

  const onSaveCallback = useCallback(
    (translationSet: TranslationSetInput) => {
      updateTranslationSetMutation({
        variables: {
          uuid: translationSetUuid,
          input: translationSet,
        },
      });
    },
    [translationSetUuid, updateTranslationSetMutation],
  );

  const [translationSetInput, setTranslationSetInput] = useState(
    data?.TranslationSet,
  );

  if (data && !data.TranslationSet) {
    onOpenChange(false);
    addErrorToast({
      message: "Object not found",
      callsite: "translate_text_dialog",
    });
    return null;
  }

  if (!translationSetInput) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <TranslationTextField
          label={label}
          value={translationSetInput}
          onChange={(translationSet) => {
            setTranslationSetInput(translationSet);
          }}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid={saveButtonDataTestId}
            onClick={() => {
              if (translationSetInput) {
                onSaveCallback(translationSetInput);
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TranslateTextDialog;

gql`
  query TranslationSet($uuid: String!) {
    TranslationSet(uuid: $uuid) {
      ...TranslationSet
    }
  }
  mutation updateTranslationSet($uuid: String!, $input: TranslationSetInput!) {
    updateTranslationSet(uuid: $uuid, input: $input) {
      success
      translationSet {
        ...TranslationSet
      }
    }
  }
`;
